import { useState, useEffect, useCallback, useRef } from 'react';
import { chatAPI } from '../../core/api/api';
import { useAuth } from '../../core/context/AuthContext';

export const useChat = () => {
  const { user } = useAuth();
  const [pendientes, setPendientes] = useState([]);
  const [chatActivo, setChatActivo] = useState(null); // solicitud seleccionada
  const [mensajes, setMensajes] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const pollingRef = useRef(null);
  const audioRef = useRef(null);
  const prevPendientesRef = useRef(0);
  const primeraCargaRef = useRef(true);

  const reproducirSonido = useCallback(() => {
    if (audioRef.current === null) {
      audioRef.current = new Audio('/sounds/Notifications_WP.wav');
    }
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  }, []);

  const cargarPendientes = useCallback(async () => {
    try {
      const { data } = await chatAPI.getPendientes();
      const nuevoTotal = data.length;
      if (!primeraCargaRef.current && nuevoTotal > prevPendientesRef.current) {
        reproducirSonido();
      }
      prevPendientesRef.current = nuevoTotal;
      primeraCargaRef.current = false;
      setPendientes(data.map(s => {
        const paciente = s.Paciente ?? s.paciente;
        return {
          id: s.Id ?? s.id,
          telefono: s.Telefono ?? s.telefono,
          fechaSolicitud: s.FechaSolicitud ?? s.fechaSolicitud,
          // Feature: solicitud-token-obra-social. Datos del paciente del turno vinculado.
          tipo: s.Tipo ?? s.tipo,
          idTurno: s.IdTurno ?? s.idTurno,
          paciente: paciente ? {
            nombre: paciente.Nombre ?? paciente.nombre,
            dni: paciente.Dni ?? paciente.dni,
            obraSocial: paciente.ObraSocial ?? paciente.obraSocial,
            estudio: paciente.Estudio ?? paciente.estudio,
            fecha: paciente.Fecha ?? paciente.fecha,
          } : null,
        };
      }));
    } catch {}
  }, [reproducirSonido]);

  const cargarMensajes = useCallback(async (solicitudId) => {
    try {
      const { data } = await chatAPI.getMensajes(solicitudId);
      setMensajes(data.map(m => ({
        id: m.Id ?? m.id,
        remitente: m.Remitente ?? m.remitente,
        mensaje: m.Mensaje ?? m.mensaje,
        imagen: m.Imagen ?? m.imagen,
        fecha: m.Fecha ?? m.fecha,
      })));
    } catch {}
  }, []);

  // Polling: pendientes cada 10s, mensajes cada 3s si hay chat activo
  useEffect(() => {
    cargarPendientes();
    pollingRef.current = setInterval(cargarPendientes, 10000);
    return () => clearInterval(pollingRef.current);
  }, [cargarPendientes]);

  useEffect(() => {
    if (!chatActivo) return;
    cargarMensajes(chatActivo.id);
    const interval = setInterval(() => cargarMensajes(chatActivo.id), 3000);
    return () => clearInterval(interval);
  }, [chatActivo, cargarMensajes]);

  const aceptarChat = async (solicitud) => {
    await chatAPI.asignarOperador({ SolicitudId: solicitud.id, OperadorId: user?.id });
    setChatActivo(solicitud);
    setPendientes(prev => prev.filter(p => p.id !== solicitud.id));
  };

  const enviarMensaje = async (texto) => {
    if (!chatActivo || !texto.trim()) return;
    const payload = { SolicitudId: chatActivo.id, Remitente: 'operador', Mensaje: texto, Telefono: chatActivo.telefono };
    await chatAPI.enviarMensaje(payload);
    await cargarMensajes(chatActivo.id);
  };

  const finalizarChat = async () => {
    if (!chatActivo) return;
    await chatAPI.finalizar(chatActivo.id);
    setChatActivo(null);
    setMensajes([]);
    cargarPendientes();
  };

  const totalPendientes = pendientes.length;

  return { pendientes, chatActivo, mensajes, isOpen, setIsOpen, aceptarChat, enviarMensaje, finalizarChat, totalPendientes };
};
